// 0.6 admits homophones and inflection slips (their/there = 0.6, cat/cats = 0.75)
const CLOSE_SIMILARITY_THRESHOLD = 0.6;

// No spaces between words: whitespace splitting would make the sentence one token.
const NO_WHITESPACE_LANGS = new Set(['ja', 'zh']);

// Lowercase, strip annotations and punctuation (keeps intra-word ' and -); ja/zh via Intl.Segmenter.
export function tokenize(text, lang) {
  if (!text) return [];
  // Denylist, not \p{L}: Babel 6 mangles unicode property escapes.
  const cleaned = text
    .toLowerCase()
    .replace(/[‘’ʼ]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\[[^\]]*\]|\([^)]*\)/g, ' ')
    .replace(/[.,!?;:"«»()[\]{}<>/\\|@#$%^&*_+=~`…·]+/g, ' ')
    // Full-width punctuation used by CJK captions and ASR.
    .replace(/[。、,！？：；「」『』（）【】《》〈〉・～]+/g, ' ');

  if (NO_WHITESPACE_LANGS.has(lang)) {
    return tokenizeNoWhitespace(cleaned, lang);
  }

  return cleaned
    .split(/\s+/)
    .map(w => w.replace(/^['-]+|['-]+$/g, ''))
    .filter(Boolean);
}

function tokenizeNoWhitespace(cleaned, lang) {
  const stripped = cleaned.replace(/\s+/g, '');
  if (!stripped) return [];

  if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
    const segmenter = new Intl.Segmenter(lang, { granularity: 'word' });
    return Array.from(segmenter.segment(stripped))
      .filter(s => s.isWordLike)
      .map(s => s.segment);
  }

  return Array.from(stripped);
}

/** @returns {{accuracy: number|null, refWords: Array<{word, status: 'match'|'close'|'missed', hyp?: string}>, extraWords: string[], counts}} accuracy is null for an empty reference */
export function scoreAttempt(referenceText, spokenText, lang) {
  const ref = tokenize(referenceText, lang);
  const hyp = tokenize(spokenText, lang);

  if (ref.length === 0) {
    return {
      accuracy: null,
      refWords: [],
      extraWords: hyp,
      counts: { match: 0, close: 0, missed: 0, extra: hyp.length, refTotal: 0 }
    };
  }

  const ops = alignWords(ref, hyp);

  const refWords = [];
  const extraWords = [];
  const counts = { match: 0, close: 0, missed: 0, extra: 0, refTotal: ref.length };

  ops.forEach(op => {
    if (op.type === 'match') {
      counts.match++;
      refWords.push({ word: op.ref, status: 'match' });
    } else if (op.type === 'sub') {
      if (wordSimilarity(op.ref, op.hyp) >= CLOSE_SIMILARITY_THRESHOLD) {
        counts.close++;
        refWords.push({ word: op.ref, status: 'close', hyp: op.hyp });
      } else {
        counts.missed++;
        refWords.push({ word: op.ref, status: 'missed', hyp: op.hyp });
      }
    } else if (op.type === 'del') {
      counts.missed++;
      refWords.push({ word: op.ref, status: 'missed' });
    } else { // ins
      counts.extra++;
      extraWords.push(op.hyp);
    }
  });

  const accuracy = Math.round((100 * (counts.match + 0.5 * counts.close)) / counts.refTotal);

  return { accuracy, refWords, extraWords, counts };
}

// Word-level Levenshtein; ops in reference order: match | sub | del | ins.
function alignWords(ref, hyp) {
  const n = ref.length;
  const m = hyp.length;

  const dp = [];
  for (let i = 0; i <= n; i++) {
    dp.push(new Array(m + 1).fill(0));
    dp[i][0] = i;
  }
  for (let j = 0; j <= m; j++) dp[0][j] = j;

  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      if (ref[i - 1] === hyp[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  const ops = [];
  let i = n;
  let j = m;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && ref[i - 1] === hyp[j - 1] && dp[i][j] === dp[i - 1][j - 1]) {
      ops.push({ type: 'match', ref: ref[i - 1], hyp: hyp[j - 1] });
      i--; j--;
    } else if (i > 0 && j > 0 && dp[i][j] === dp[i - 1][j - 1] + 1) {
      ops.push({ type: 'sub', ref: ref[i - 1], hyp: hyp[j - 1] });
      i--; j--;
    } else if (i > 0 && dp[i][j] === dp[i - 1][j] + 1) {
      ops.push({ type: 'del', ref: ref[i - 1] });
      i--;
    } else {
      ops.push({ type: 'ins', hyp: hyp[j - 1] });
      j--;
    }
  }
  return ops.reverse();
}

function wordSimilarity(a, b) {
  if (a === b) return 1;
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  return 1 - charLevenshtein(a, b) / maxLen;
}

function charLevenshtein(a, b) {
  let prev = [];
  for (let j = 0; j <= b.length; j++) prev.push(j);

  for (let i = 1; i <= a.length; i++) {
    const curr = [i];
    for (let j = 1; j <= b.length; j++) {
      curr.push(
        a[i - 1] === b[j - 1]
          ? prev[j - 1]
          : 1 + Math.min(prev[j - 1], prev[j], curr[j - 1])
      );
    }
    prev = curr;
  }
  return prev[b.length];
}
